// Calendar membership, never nearest-price/nearest-date guessing across gaps.
export function methodPointTimestamp(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const milliseconds = Date.parse(`${value}T00:00:00Z`);
    return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value ? milliseconds / 1000 : null;
}

export function methodPointPeriod(value, resolution) {
    const timestamp = methodPointTimestamp(value);
    if (timestamp === null) return null;
    const date = new Date(timestamp * 1000);
    if (resolution === 'M') return date.toISOString().slice(0, 7);
    if (resolution === 'W') date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 6) % 7);
    return date.toISOString().slice(0, 10);
}

export function buildChartingMethodPoints({ candles = [], support, supportVisible = true, resolution = 'M', trendAnchors = [], entryPoint }) {
    const points = [];
    const period = methodPointPeriod(support?.originDate, resolution);
    const supportBar = period === null ? null : candles.find(bar => methodPointPeriod(bar.time, resolution) === period);
    if (supportVisible && supportBar && typeof support?.price === 'number' && Number.isFinite(support.price) && support.price > 0) {
        points.push({ id: 'method-support', label: 'Origen del soporte', shortLabel: 'S', time: supportBar.time, date: support.originDate, price: support.price, color: '#dc2626' });
    }
    for (const [index, anchor] of trendAnchors.entries()) {
        if (!anchor || !Number.isFinite(anchor.price) || anchor.price <= 0 || !candles.some(bar => bar.time === anchor.time)) continue;
        if (anchor.date && methodPointPeriod(anchor.date, resolution) !== methodPointPeriod(anchor.time, resolution)) continue;
        points.push({ id: `method-lt-${index + 1}`, label: `Anclaje ${index + 1} de la LT`, shortLabel: `LT${index + 1}`, time: anchor.time, date: anchor.date || anchor.time, price: anchor.price, color: '#0284c7' });
    }
    const latest = candles[candles.length - 1];
    if (latest && Number.isFinite(entryPoint?.price) && entryPoint.price > 0) {
        points.push({ id: 'method-lt-entry', label: 'Entrada LT', shortLabel: 'Entrada LT', shape: 'circle', time: latest.time, date: null, price: entryPoint.price, color: '#0284c7', description: 'Nivel de entrada del análisis actual; no es un anclaje histórico.' });
    }
    return points;
}

export function toggleMethodPointSelection(ids, id) {
    return ids.includes(id) ? ids.filter(value => value !== id) : [...ids, id];
}

export function highlightedMethodPoints(points, selectedIds, hoveredId) {
    const hovered = points.find(point => point.id === hoveredId);
    return points.filter(point => selectedIds.includes(point.id) || (hovered && point.time === hovered.time));
}

// Both layers use the native render cycle and the price series' coordinates.
// No autoscale contribution: annotations never alter the chart's price range.
export class ChartingMethodPoints {
    constructor() {
        this.points = [];
        this.items = [];
        this.activeIds = [];
        this.interactive = true;
        this.background = '#ffffff';
        this.views = [
            { zOrder: () => 'bottom', renderer: () => ({ draw: target => this.drawShade(target) }) },
            { zOrder: () => 'top', renderer: () => this }
        ];
    }

    attached({ chart, series, requestUpdate }) {
        this.chart = chart;
        this.series = series;
        this.requestUpdate = requestUpdate;
        this.updateAllViews();
    }

    detached() {
        this.chart = null;
        this.series = null;
        this.requestUpdate = null;
        this.items = [];
    }

    paneViews() { return this.views; }

    setPoints(points, { dark = false, interactive = true } = {}) {
        this.points = points;
        this.background = dark ? '#08111f' : '#ffffff';
        this.interactive = interactive;
        this.activeIds = this.activeIds.filter(id => points.some(point => point.id === id));
        this.updateAllViews();
        this.requestUpdate?.();
    }

    setActive(ids) {
        if (this.activeIds.join('|') === ids.join('|')) return;
        this.activeIds = [...ids];
        this.requestUpdate?.();
    }

    updateAllViews() {
        this.items = this.series && this.chart ? this.points.flatMap(point => {
            const x = this.chart.timeScale().timeToCoordinate(point.time);
            const y = this.series.priceToCoordinate(point.price);
            return Number.isFinite(x) && Number.isFinite(y) ? [{ ...point, x, y }] : [];
        }) : [];
    }

    hitTest(x, y) {
        if (!this.interactive) return null;
        const item = this.items.find(point => Math.abs(point.x - x) <= 12 && Math.abs(point.y - y) <= 12);
        return item ? { externalId: item.id, cursorStyle: 'pointer', zOrder: 'top' } : null;
    }

    drawShade(target) {
        const active = this.items.filter(point => this.activeIds.includes(point.id));
        if (!active.length) return;
        const spacing = this.chart.timeScale().options().barSpacing;
        target.useMediaCoordinateSpace(({ context, mediaSize }) => {
            const width = Math.max(8, Math.min(56, spacing * 0.9));
            context.save();
            context.globalAlpha = 0.09;
            const paintedTimes = new Set();
            for (const point of active) {
                if (paintedTimes.has(point.time)) continue;
                paintedTimes.add(point.time);
                context.fillStyle = point.color;
                context.fillRect(point.x - width / 2, 0, width, mediaSize.height);
            }
            context.restore();
        });
    }

    draw(target) {
        target.useMediaCoordinateSpace(({ context, mediaSize }) => {
            context.save();
            try {
                for (const item of this.items) {
                    if (item.x < 0 || item.x > mediaSize.width || item.y < 0 || item.y > mediaSize.height) continue;
                    // Concentric outlines keep coincident support/LT roles visible.
                    const radius = item.id === 'method-support' ? 5 : 7;
                    context.beginPath();
                    if (item.shape === 'circle') {
                        context.arc(item.x, item.y, 6, 0, Math.PI * 2);
                    } else {
                        context.moveTo(item.x, item.y - radius);
                        context.lineTo(item.x + radius, item.y);
                        context.lineTo(item.x, item.y + radius);
                        context.lineTo(item.x - radius, item.y);
                    }
                    context.closePath();
                    context.strokeStyle = this.background;
                    context.lineWidth = 4;
                    context.stroke();
                    context.strokeStyle = item.color;
                    context.lineWidth = this.activeIds.includes(item.id) ? 2.5 : 1.5;
                    context.stroke();
                }
            } finally {
                context.restore();
            }
        });
    }
}
